import {
  AccountUserActions,
  AccountUserActionTypes,
} from './account-user.actions';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  UserLicencePermission,
  LicensePermission,
} from '@account-users/account/models/LicensePermissionList';
import { IAccountUser } from '@account-users/account/models/IAccountUser';
import { License } from '@account-users/account/models/License';
import { AccountLicense } from '@account-users/account/models/AccountLicense';
import { IUserRole, RoleType } from '@account-users/user/models/UserRole';
import { LicenseTypeConfig } from '@account-users/account/models/LicenseTypeConfig';

export const ACCOUNT_USER_FEATURE_KEY = 'account-user';

export interface AccountUserState {
  userLicensePermissions: UserLicencePermission[];
  allLicenses: License[];
  licensePermissions: LicensePermission[];
  accountLicenses: AccountLicense[];
  licenseTypes: LicenseTypeConfig[];
  accountUser: IAccountUser;
  accountUsers: IAccountUser[];
  userRoles: IUserRole[];
  isLoadingAccountUser: boolean;
  isLoadingAccountUserPermissions: boolean;
  loadingError: boolean;
  isUpdatingAccountRole: boolean;
}

export const initialState: AccountUserState = {
  userLicensePermissions: [],
  allLicenses: [],
  licensePermissions: [],
  accountLicenses: [],
  licenseTypes: [],
  accountUser: null,
  accountUsers: [],
  userRoles: [],
  isLoadingAccountUser: false,
  isLoadingAccountUserPermissions: false,
  loadingError: false,
  isUpdatingAccountRole: false,
};

export function accountUserReducer(
  state: AccountUserState = initialState,
  action: AccountUserActions,
): AccountUserState {
  switch (action.type) {
    case AccountUserActionTypes.LoadAccountUser: {
      return {
        ...state,
        isLoadingAccountUser: true,
      };
    }

    case AccountUserActionTypes.LoadAccountUserSuccess: {
      return {
        ...state,
        isLoadingAccountUser: false,
        accountUser: action.payload,
      };
    }

    case AccountUserActionTypes.LoadAccountUserPermissions: {
      return {
        ...state,
        isLoadingAccountUserPermissions: true,
      };
    }

    case AccountUserActionTypes.LoadAccountUserPermissionsSuccess: {
      return {
        ...state,
        isLoadingAccountUserPermissions: false,
        userLicensePermissions: action.payload,
      };
    }

    case AccountUserActionTypes.LoadAllAccountUserLicenses: {
      return {
        ...state,
        isLoadingAccountUser: true,
        isLoadingAccountUserPermissions: true,
        loadingError: false,
      };
    }

    case AccountUserActionTypes.LoadAllAccountUserLicensesSuccess: {
      const {
        accountUser,
        userLicenses,
        allLicenses,
        licensePermissions,
        accountLicenses,
        userRoles,
        accountUsers,
        licenseTypes,
      } = action.payload;
      return {
        ...state,
        accountUser: accountUser,
        userLicensePermissions: userLicenses,
        allLicenses: allLicenses,
        licensePermissions: licensePermissions,
        accountLicenses: accountLicenses,
        userRoles: userRoles,
        accountUsers: accountUsers,
        licenseTypes: licenseTypes,
        isLoadingAccountUser: false,
        isLoadingAccountUserPermissions: false,
        loadingError: false,
      };
    }

    case AccountUserActionTypes.LoadAllAccountUserLicensesFailure: {
      return {
        ...state,
        isLoadingAccountUser: false,
        isLoadingAccountUserPermissions: false,
        loadingError: true,
      };
    }

    case AccountUserActionTypes.UpdateAccountUserLicensePermissions: {
      return {
        ...state,
        isLoadingAccountUserPermissions: true,
      };
    }
    case AccountUserActionTypes.UpdateAccountUserLicensePermissionsSuccess: {
      return {
        ...state,
        isLoadingAccountUserPermissions: false,
      };
    }

    case AccountUserActionTypes.UpdateAccountUserSectionPermissions: {
      return {
        ...state,
        isLoadingAccountUserPermissions: true,
      };
    }
    case AccountUserActionTypes.UpdateAccountUserSectionPermissionsSuccess: {
      return {
        ...state,
        isLoadingAccountUserPermissions: false,
      };
    }

    case AccountUserActionTypes.UpdateAccountUserRoles: {
      return {
        ...state,
        isUpdatingAccountRole: true,
      };
    }

    case AccountUserActionTypes.UpdateAccountUserRolesFailure: {
      return {
        ...state,
        isUpdatingAccountRole: false,
      };
    }

    case AccountUserActionTypes.LoadAccountUserAfterRoleSuccess: {
      const { user, userLicensePermissions } = action.payload;
      return {
        ...state,
        accountUser: user,
        userLicensePermissions: userLicensePermissions,
        isUpdatingAccountRole: false,
      };
    }

    case AccountUserActionTypes.LoadAccountUserAfterRoleFailure: {
      return {
        ...state,
        isUpdatingAccountRole: false,
      };
    }
  }

  return state;
}

export const getAccountUserState = createFeatureSelector<AccountUserState>(
  ACCOUNT_USER_FEATURE_KEY,
);

export const getAccountUser = createSelector(
  getAccountUserState,
  (state) => state.accountUser,
);

export const getAccountUserLicensePermissions = createSelector(
  getAccountUserState,
  (state) => state.userLicensePermissions,
);

export const getAllLicenses = createSelector(
  getAccountUserState,
  (state) => state.allLicenses,
);

export const getLicensePermissions = createSelector(
  getAccountUserState,
  (state) => state.licensePermissions,
);

export const getAccountLicenses = createSelector(
  getAccountUserState,
  (state) => state.accountLicenses,
);

export const getUserRoles = createSelector(getAccountUserState, (state) =>
  state.userRoles.filter((role) => role.type === RoleType.AccountUser),
);

export const getAccountUsers = createSelector(
  getAccountUserState,
  (state) => state.accountUsers,
);

export const getLicenseTypes = createSelector(
  getAccountUserState,
  (state) => state.licenseTypes,
);

export const getIsLoadingAccountUserPermissions = createSelector(
  getAccountUserState,
  (state) => state.isLoadingAccountUserPermissions,
);

export const getIsLoading = createSelector(
  getAccountUserState,
  (state) =>
    !!(state.isLoadingAccountUserPermissions && state.isLoadingAccountUser),
);

export const getIsError = createSelector(
  getAccountUserState,
  (state) => state.loadingError,
);

export const getIsUpdatingRole = createSelector(
  getAccountUserState,
  (state) => state.isUpdatingAccountRole,
);

export const isDataLoaded = createSelector(
  getAccountUserState,
  getIsLoading,
  (accountUsers, loadingError, isLoading) =>
    accountUsers && !loadingError && !isLoading,
);
